import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { caseService, researchService, documentService, contractService, billingService, settingsService, humanVerificationService } from '../services/api';
import { formatCaseStatus } from '../utils/caseStatus';
import '../styles/CaseDetail.css';

export default function CaseDetail() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [caseData, setCaseData] = useState(null);
  const [researchCases, setResearchCases] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [touchPointStages, setTouchPointStages] = useState([]);
  const [caseWorkflow, setCaseWorkflow] = useState(null);
  const [workflowScheduleItems, setWorkflowScheduleItems] = useState([]);
  const [billingSummary, setBillingSummary] = useState(null);
  const [activeTab, setActiveTab] = useState(() => new URLSearchParams(location.search).get('tab') || 'details');
  const [activeTouchPointStage, setActiveTouchPointStage] = useState(1);
  const [activeBillingTab, setActiveBillingTab] = useState('setup');
  const [activeDocumentTab, setActiveDocumentTab] = useState('verification');
  const [activeWorkflowTab, setActiveWorkflowTab] = useState('aiCaseWorkflow');
  const [activeCalendarTab, setActiveCalendarTab] = useState('SCHEDULED_EVENT');
  const [loading, setLoading] = useState(true);
  const researchLoading = false;
  const [documentLoading, setDocumentLoading] = useState(false);
  const [error, setError] = useState('');
  const [successNotice, setSuccessNotice] = useState('');
  const [humanVerificationTasks, setHumanVerificationTasks] = useState([]);
  const [humanVerificationError, setHumanVerificationError] = useState('');
  const [humanVerificationForm, setHumanVerificationForm] = useState({ taskType: 'AI_OUTPUT_REVIEW', artifactKey: 'DRAFT:case', instructions: '', assignedOrganization: 'Internal Review Team', assignedReviewerEmail: '', priority: 'NORMAL', dueAt: '' });
  const [researchError, setResearchError] = useState('');
  const [documentError, setDocumentError] = useState('');
  const [verificationCompanies, setVerificationCompanies] = useState([]);
  const [draftProviders, setDraftProviders] = useState([]);
  const [researchProviders, setResearchProviders] = useState([]);
  const [reviewProviders, setReviewProviders] = useState([]);
  const [contractReviewProviders, setContractReviewProviders] = useState([]);
  const [paymentProcessors, setPaymentProcessors] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedResearchProviderId, setSelectedResearchProviderId] = useState('');
  const [selectedDraftProviderId, setSelectedDraftProviderId] = useState('');
  const [selectedReviewProviderId, setSelectedReviewProviderId] = useState('');
  const [selectedReviewDocumentIds, setSelectedReviewDocumentIds] = useState([]);
  const [reviewInstructions, setReviewInstructions] = useState('');
  const reviewLoading = false;
  const [reviewError, setReviewError] = useState('');
  const [contractLoading, setContractLoading] = useState(false);
  const [contractError, setContractError] = useState('');
  const [workflowError, setWorkflowError] = useState('');
  const [scheduleError, setScheduleError] = useState('');
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [billingError, setBillingError] = useState('');
  const [billingLoading, setBillingLoading] = useState(false);
  const [selectedContractReviewProviderId, setSelectedContractReviewProviderId] = useState('');
  const [selectedPaymentProcessorId, setSelectedPaymentProcessorId] = useState('');
  const [contractReviewInstructions, setContractReviewInstructions] = useState('');
  const [draftLoading, setDraftLoading] = useState(false);
  const [draftError, setDraftError] = useState('');
  const [uploadForm, setUploadForm] = useState({
    file: null,
    documentType: 'EVIDENCE',
    notes: '',
  });
  const [contractForm, setContractForm] = useState({
    file: null,
    title: '',
    contractType: '',
    counterparty: '',
    notes: '',
  });
  const [billingProfileForm, setBillingProfileForm] = useState({
    billingType: 'HOURLY',
    hourlyRate: '',
    flatFeeAmount: '',
    retainerAmount: '',
    subscriptionAmount: '',
    subscriptionInterval: 'Monthly',
    contingencyPercentage: '',
    recoveryAmount: '',
    hybridBaseFee: '',
    invoiceRecipientName: '',
    invoiceRecipientEmail: '',
    billingNotes: '',
    status: 'ACTIVE',
  });
  const [timeEntryForm, setTimeEntryForm] = useState({
    activityType: 'CASE_WORK',
    description: '',
    hours: '',
    rate: '',
    billable: true,
    workDate: new Date().toISOString().slice(0, 10),
    status: 'DRAFT',
  });
  const [scheduleForm, setScheduleForm] = useState({
    title: '',
    description: '',
    scheduledAt: '',
    endAt: '',
    location: '',
    partyName: '',
    priority: 'NORMAL',
    reminderAt: '',
  });

  useEffect(() => {
    loadCaseData();
  }, [id]);

  useEffect(() => {
    humanVerificationService.getCaseTasks(id)
      .then((response) => setHumanVerificationTasks(response.data || []))
      .catch(() => setHumanVerificationError('Unable to load human verification requests.'));
  }, [id]);

  useEffect(() => {
    const context = location.state?.aiOutcome || location.state?.aiContext || {};
    const params = new URLSearchParams(location.search);
    const tabFromQuery = params.get('tab');
    const documentTabFromQuery = params.get('documentTab');

    if (tabFromQuery || context.tab) {
      setActiveTab(tabFromQuery || context.tab);
    }
    if (documentTabFromQuery || context.documentTab) {
      setActiveDocumentTab(documentTabFromQuery || context.documentTab);
    }
    if (location.state?.aiOutcome?.message) {
      setSuccessNotice(location.state.aiOutcome.message);
    }

    if (location.state) {
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }
  }, [location, navigate]);

  const loadCaseData = async () => {
    setLoading(true);
    setError('');
    try {
      const caseResp = await caseService.getCaseById(id);
      setCaseData(caseResp.data);

      const [researchResp, docResp, contractResp, touchPointResp, workflowResp, scheduleResp, billingResp] = await Promise.allSettled([
        researchService.getResearchCasesForCase(id),
        documentService.getDocumentsForCase(id),
        contractService.getContractsForCase(id),
        caseService.getCaseTouchPoints(id),
        caseService.getCaseWorkflow(id),
        caseService.getWorkflowScheduleItems(id),
        billingService.getBillingSummary(id),
      ]);

      setResearchCases(researchResp.status === 'fulfilled' ? researchResp.value.data : []);
      setDocuments(docResp.status === 'fulfilled' ? docResp.value.data : []);
      setContracts(contractResp.status === 'fulfilled' ? contractResp.value.data : []);
      setTouchPointStages(touchPointResp.status === 'fulfilled' ? touchPointResp.value.data : []);
      setCaseWorkflow(workflowResp.status === 'fulfilled' && workflowResp.value.status !== 204 ? workflowResp.value.data : null);
      setWorkflowScheduleItems(scheduleResp.status === 'fulfilled' ? scheduleResp.value.data : []);
      if (billingResp.status === 'fulfilled') {
        setBillingSummary(billingResp.value.data);
        syncBillingProfileForm(billingResp.value.data?.profile, caseResp.data);
      }
      loadVerificationCompanies();
      loadResearchProviders();
      loadDraftProviders();
      loadReviewProviders();
      loadContractReviewProviders();
      loadPaymentProcessors();
    } catch (err) {
      const responseMessage = typeof err.response?.data === 'string'
        ? err.response.data
        : err.response?.data?.message;
      setError(responseMessage || 'Failed to load case data');
    } finally {
      setLoading(false);
    }
  };

  const syncBillingProfileForm = (profile, caseInfo) => {
    setBillingProfileForm({
      billingType: profile?.billingType || 'HOURLY',
      hourlyRate: profile?.hourlyRate || '',
      flatFeeAmount: profile?.flatFeeAmount || '',
      retainerAmount: profile?.retainerAmount || '',
      subscriptionAmount: profile?.subscriptionAmount || '',
      subscriptionInterval: profile?.subscriptionInterval || 'Monthly',
      contingencyPercentage: profile?.contingencyPercentage || '',
      recoveryAmount: profile?.recoveryAmount || '',
      hybridBaseFee: profile?.hybridBaseFee || '',
      invoiceRecipientName: profile?.invoiceRecipientName || caseInfo?.clientName || '',
      invoiceRecipientEmail: profile?.invoiceRecipientEmail || caseInfo?.clientEmail || '',
      billingNotes: profile?.billingNotes || '',
      status: profile?.status || 'ACTIVE',
    });
  };

  const loadVerificationCompanies = async () => {
    try {
      const response = await settingsService.getVerificationCompanies(true);
      const companies = Array.isArray(response.data) ? response.data : [];
      setVerificationCompanies(companies);
      if (!selectedCompanyId && companies.length > 0) {
        setSelectedCompanyId(String(companies[0].id));
      }
    } catch (err) {
      setVerificationCompanies([]);
    }
  };

  const loadDraftProviders = async () => {
    try {
      const response = await settingsService.getDraftLlmProviders(true);
      const providers = Array.isArray(response.data) ? response.data : [];
      setDraftProviders(providers);
      if (!selectedDraftProviderId && providers.length > 0) {
        setSelectedDraftProviderId(String(providers[0].id));
      }
    } catch (err) {
      setDraftProviders([]);
    }
  };

  const loadResearchProviders = async () => {
    try {
      const response = await settingsService.getResearchLlmProviders(true);
      const providers = Array.isArray(response.data) ? response.data : [];
      setResearchProviders(providers);
      if (!selectedResearchProviderId && providers.length > 0) {
        setSelectedResearchProviderId(String(providers[0].id));
      }
    } catch (err) {
      setResearchProviders([]);
    }
  };

  const loadReviewProviders = async () => {
    try {
      const response = await settingsService.getDocumentReviewLlmProviders(true);
      const providers = Array.isArray(response.data) ? response.data : [];
      setReviewProviders(providers);
      if (!selectedReviewProviderId && providers.length > 0) {
        setSelectedReviewProviderId(String(providers[0].id));
      }
    } catch (err) {
      setReviewProviders([]);
    }
  };

  const loadContractReviewProviders = async () => {
    try {
      const response = await settingsService.getContractReviewLlmProviders(true);
      const providers = Array.isArray(response.data) ? response.data : [];
      setContractReviewProviders(providers);
      if (!selectedContractReviewProviderId && providers.length > 0) {
        setSelectedContractReviewProviderId(String(providers[0].id));
      }
    } catch (err) {
      setContractReviewProviders([]);
    }
  };

  const loadPaymentProcessors = async () => {
    try {
      const response = await settingsService.getPaymentProcessors(true);
      const providers = Array.isArray(response.data) ? response.data : [];
      setPaymentProcessors(providers);
      if (!selectedPaymentProcessorId && providers.length > 0) {
        setSelectedPaymentProcessorId(String(providers[0].id));
      }
    } catch (err) {
      setPaymentProcessors([]);
    }
  };

  const handleGenerateResearch = async () => {
    if (!selectedResearchProviderId) {
      setResearchError('Add and select a Research provider first.');
      return;
    }

    navigate(`/case/${id}/ai-request-preview`, {
      state: { action: 'RESEARCH', providerId: selectedResearchProviderId, returnTab: 'research' },
    });
  };

  const verificationArtifacts = [
    { key: 'DRAFT:case', type: 'DRAFT', id: null, label: 'Current case draft' },
    ...researchCases.map((item) => ({ key: `RESEARCH_CASE:${item.id}`, type: 'RESEARCH_CASE', id: item.id, label: `Research: ${item.caseTitle}` })),
    ...documents.map((item) => ({ key: `DOCUMENT:${item.id}`, type: 'DOCUMENT', id: item.id, label: `Document: ${item.fileName}` })),
    ...contracts.map((item) => ({ key: `CONTRACT:${item.id}`, type: 'CONTRACT', id: item.id, label: `Contract: ${item.title}` })),
  ];

  const requestHumanVerification = async (e) => {
    e.preventDefault();
    const artifact = verificationArtifacts.find((item) => item.key === humanVerificationForm.artifactKey);
    if (!artifact) { setHumanVerificationError('Select an artifact to verify.'); return; }
    try {
      const response = await humanVerificationService.requestTask(id, {
        taskType: humanVerificationForm.taskType,
        artifactType: artifact.type,
        artifactId: artifact.id,
        artifactLabel: artifact.label,
        instructions: humanVerificationForm.instructions,
        assignedOrganization: humanVerificationForm.assignedOrganization,
        assignedReviewerEmail: humanVerificationForm.assignedReviewerEmail,
        priority: humanVerificationForm.priority,
        dueAt: humanVerificationForm.dueAt || null,
      });
      setHumanVerificationTasks((current) => [response.data, ...current]);
      setHumanVerificationForm((current) => ({ ...current, instructions: '', dueAt: '' }));
      setHumanVerificationError('');
    } catch (err) { setHumanVerificationError(err.response?.data?.message || 'Unable to create the verification request.'); }
  };

  const decideHumanVerification = async (taskId, decision) => {
    try {
      const response = await humanVerificationService.attorneyDecision(taskId, decision, 'Attorney decision recorded in LibraLegal.');
      setHumanVerificationTasks((current) => current.map((task) => task.id === taskId ? response.data : task));
    } catch (err) { setHumanVerificationError(err.response?.data?.message || 'Unable to record attorney decision.'); }
  };

  const handleUploadChange = (e) => {
    const { name, value, files } = e.target;
    setUploadForm((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  const handleContractChange = (e) => {
    const { name, value, files } = e.target;
    setContractForm((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  const handleBillingProfileChange = (e) => {
    const { name, value } = e.target;
    setBillingProfileForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleTimeEntryChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTimeEntryForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleScheduleChange = (e) => {
    const { name, value } = e.target;
    setScheduleForm((prev) => ({ ...prev, [name]: value }));
  };

  const loadBillingSummary = async () => {
    const response = await billingService.getBillingSummary(id);
    setBillingSummary(response.data);
    syncBillingProfileForm(response.data?.profile, caseData);
  };

  const handleSaveBillingProfile = async (e) => {
    e.preventDefault();
    setBillingLoading(true);
    setBillingError('');
    try {
      await billingService.saveBillingProfile(id, normalizeBillingProfile(billingProfileForm));
      await loadBillingSummary();
    } catch (err) {
      const responseMessage = typeof err.response?.data === 'string' ? err.response.data : err.response?.data?.message;
      setBillingError(responseMessage || 'Failed to save billing profile');
    } finally {
      setBillingLoading(false);
    }
  };

  const handleAddTimeEntry = async (e) => {
    e.preventDefault();
    setBillingLoading(true);
    setBillingError('');
    try {
      await billingService.addTimeEntry(id, {
        ...normalizeTimeEntry(timeEntryForm),
        userId: user?.userId || caseData.createdByUserId,
      });
      setTimeEntryForm({ activityType: 'CASE_WORK', description: '', hours: '', rate: '', billable: true, workDate: new Date().toISOString().slice(0, 10), status: 'DRAFT' });
      await loadBillingSummary();
    } catch (err) {
      const responseMessage = typeof err.response?.data === 'string' ? err.response.data : err.response?.data?.message;
      setBillingError(responseMessage || 'Failed to add time entry');
    } finally {
      setBillingLoading(false);
    }
  };

  const handleGenerateInvoice = async () => {
    setBillingLoading(true);
    setBillingError('');
    try {
      await billingService.generateInvoice(id, user?.userId || caseData.createdByUserId);
      await loadBillingSummary();
      const response = await documentService.getDocumentsForCase(id);
      setDocuments(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      const responseMessage = typeof err.response?.data === 'string' ? err.response.data : err.response?.data?.message;
      setBillingError(responseMessage || 'Failed to generate invoice');
    } finally {
      setBillingLoading(false);
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    setBillingLoading(true);
    setBillingError('');
    try {
      await billingService.sendInvoice(id, invoiceId);
      await loadBillingSummary();
    } catch (err) {
      const responseMessage = typeof err.response?.data === 'string' ? err.response.data : err.response?.data?.message;
      setBillingError(responseMessage || 'Failed to send invoice');
    } finally {
      setBillingLoading(false);
    }
  };

  const handleCreatePaymentLink = async (invoice) => {
    setBillingLoading(true);
    setBillingError('');
    try {
      const response = await billingService.createPaymentLink(id, invoice.id, {
        providerId: selectedPaymentProcessorId || null,
        amount: invoice.balanceAmount || invoice.totalAmount,
        currency: 'USD',
        paymentMethod: 'HOSTED_CHECKOUT',
      });
      await loadBillingSummary();
      if (response.data?.checkoutUrl) {
        window.open(response.data.checkoutUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      const responseMessage = typeof err.response?.data === 'string' ? err.response.data : err.response?.data?.message;
      setBillingError(responseMessage || 'Failed to create payment link');
    } finally {
      setBillingLoading(false);
    }
  };

  const handleMarkPaymentPaid = async (invoiceId, paymentId) => {
    setBillingLoading(true);
    setBillingError('');
    try {
      await billingService.markPaymentPaid(id, invoiceId, paymentId);
      await loadBillingSummary();
    } catch (err) {
      const responseMessage = typeof err.response?.data === 'string' ? err.response.data : err.response?.data?.message;
      setBillingError(responseMessage || 'Failed to record payment');
    } finally {
      setBillingLoading(false);
    }
  };

  const handleUploadDocument = async (e) => {
    e.preventDefault();
    if (!uploadForm.file || !caseData?.createdByUserId) {
      setDocumentError('Choose a file before uploading.');
      return;
    }

    setDocumentLoading(true);
    setDocumentError('');

    try {
      await documentService.uploadDocument(
        id,
        uploadForm.file,
        uploadForm.documentType,
        uploadForm.notes,
        caseData.createdByUserId);
      const response = await documentService.getDocumentsForCase(id);
      setDocuments(Array.isArray(response.data) ? response.data : []);
      setUploadForm({ file: null, documentType: 'EVIDENCE', notes: '' });
      e.target.reset();
    } catch (err) {
      const responseMessage = typeof err.response?.data === 'string'
        ? err.response.data
        : err.response?.data?.message;
      const fallbackMessage = err.response?.status === 413
        ? 'The selected file is too large. The maximum upload size is 25 MB.'
        : err.response?.status === 403
          ? 'Your account is not authorized to upload documents for this case.'
          : 'Failed to upload document. Please try again or contact your administrator.';
      setDocumentError(responseMessage || fallbackMessage);
    } finally {
      setDocumentLoading(false);
    }
  };

  const handleUploadContract = async (e) => {
    e.preventDefault();
    if (!contractForm.file || !caseData?.createdByUserId) {
      setContractError('Choose a contract file before uploading.');
      return;
    }

    setContractLoading(true);
    setContractError('');

    try {
      await contractService.uploadContract(
        id,
        contractForm.file,
        contractForm.title || contractForm.file.name,
        contractForm.contractType,
        contractForm.counterparty,
        contractForm.notes,
        caseData.createdByUserId);
      const response = await contractService.getContractsForCase(id);
      setContracts(Array.isArray(response.data) ? response.data : []);
      setContractForm({ file: null, title: '', contractType: '', counterparty: '', notes: '' });
      e.target.reset();
    } catch (err) {
      const responseMessage = typeof err.response?.data === 'string'
        ? err.response.data
        : err.response?.data?.message;
      setContractError(responseMessage || 'Failed to upload contract');
    } finally {
      setContractLoading(false);
    }
  };

  const handleDownloadDocument = async (document) => {
    const response = await documentService.downloadDocument(id, document.id);
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = window.document.createElement('a');
    link.href = url;
    link.download = document.fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleViewDocument = async (document) => {
    const response = await documentService.downloadDocument(id, document.id);
    const url = window.URL.createObjectURL(new Blob([response.data]));
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadContract = async (contract) => {
    const response = await contractService.downloadContract(id, contract.id);
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = window.document.createElement('a');
    link.href = url;
    link.download = contract.fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleViewContract = async (contract) => {
    const response = await contractService.downloadContract(id, contract.id);
    const url = window.URL.createObjectURL(new Blob([response.data]));
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleVerifyDocument = async (documentId) => {
    if (!selectedCompanyId) {
      setDocumentError('Add and select a verification company first.');
      return;
    }

    setDocumentLoading(true);
    setDocumentError('');

    try {
      const response = await documentService.verifyDocument(id, documentId, selectedCompanyId);
      setDocuments((prev) => prev.map((doc) => doc.id === documentId ? response.data : doc));
    } catch (err) {
      const responseMessage = typeof err.response?.data === 'string'
        ? err.response.data
        : err.response?.data?.message;
      setDocumentError(responseMessage || 'Failed to verify document');
    } finally {
      setDocumentLoading(false);
    }
  };

  const toggleReviewDocument = (documentId, checked) => {
    setSelectedReviewDocumentIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(documentId);
      } else {
        next.delete(documentId);
      }
      return Array.from(next);
    });
  };

  const handleReviewDocuments = async () => {
    if (!selectedReviewProviderId) {
      setReviewError('Add and select a document review LLM provider first.');
      return;
    }
    if (selectedReviewDocumentIds.length === 0) {
      setReviewError('Select at least one document to review.');
      return;
    }

    navigate(`/case/${id}/ai-request-preview`, {
      state: {
        action: 'DOCUMENT_REVIEW',
        providerId: selectedReviewProviderId,
        documentIds: selectedReviewDocumentIds,
        instructions: reviewInstructions,
        requestedByUserId: user?.userId || caseData.createdByUserId,
        returnTab: 'documents',
        returnDocumentTab: 'review',
      },
    });
  };

  const handleReviewContract = async (contractId) => {
    if (!selectedContractReviewProviderId) {
      setContractError('Add and select a contract review LLM provider first.');
      return;
    }

    navigate(`/case/${id}/ai-request-preview`, {
      state: {
        action: 'CONTRACT_REVIEW',
        providerId: selectedContractReviewProviderId,
        contractId,
        instructions: contractReviewInstructions,
        returnTab: 'contracts',
      },
    });
  };

  const handleGenerateDraft = async () => {
    if (!selectedDraftProviderId) {
      setDraftError('Add and select a draft LLM provider first.');
      return;
    }

    navigate(`/case/${id}/ai-request-preview`, {
      state: { action: 'DRAFT', providerId: selectedDraftProviderId, returnTab: 'draft' },
    });
  };

  const handleSaveDraft = async (e) => {
    e.preventDefault();
    setDraftLoading(true);
    setDraftError('');

    try {
      const response = await caseService.updateCaseDraft(id, caseData.draftContent || '');
      setCaseData(response.data);
    } catch (err) {
      const responseMessage = typeof err.response?.data === 'string'
        ? err.response.data
        : err.response?.data?.message;
      setDraftError(responseMessage || 'Failed to save draft');
    } finally {
      setDraftLoading(false);
    }
  };

  const handleStartWorkflow = async () => {
    setWorkflowError('');
    try {
      const response = await caseService.startCaseWorkflow(id);
      setCaseWorkflow(response.data);
    } catch (err) {
      const responseMessage = typeof err.response?.data === 'string'
        ? err.response.data
        : err.response?.data?.message;
      setWorkflowError(responseMessage || 'Failed to start case workflow');
    }
  };

  const handleRunWorkflow = async () => {
    setWorkflowError('');
    try {
      const response = await caseService.runCaseWorkflow(id);
      setCaseWorkflow(response.data);
      const caseResp = await caseService.getCaseById(id);
      setCaseData(caseResp.data);
    } catch (err) {
      const responseMessage = typeof err.response?.data === 'string'
        ? err.response.data
        : err.response?.data?.message;
      setWorkflowError(responseMessage || 'Failed to run case workflow');
    }
  };

  const handleCompleteHumanTask = async (taskId) => {
    setWorkflowError('');
    try {
      await caseService.completeHumanTask(id, taskId);
      const response = await caseService.getCaseWorkflow(id);
      setCaseWorkflow(response.data);
    } catch (err) {
      const responseMessage = typeof err.response?.data === 'string'
        ? err.response.data
        : err.response?.data?.message;
      setWorkflowError(responseMessage || 'Failed to complete human task');
    }
  };

  const loadWorkflowScheduleItems = async () => {
    const response = await caseService.getWorkflowScheduleItems(id);
    setWorkflowScheduleItems(Array.isArray(response.data) ? response.data : []);
  };

  const handleSaveScheduleItem = async (e) => {
    e.preventDefault();
    setScheduleLoading(true);
    setScheduleError('');
    try {
      await caseService.saveWorkflowScheduleItem(id, normalizeScheduleItem(scheduleForm, activeScheduleCategory));
      setScheduleForm({
        title: '',
        description: '',
        scheduledAt: '',
        endAt: '',
        location: '',
        partyName: '',
        priority: 'NORMAL',
        reminderAt: '',
      });
      await loadWorkflowScheduleItems();
      const workflowResp = await caseService.getCaseWorkflow(id);
      setCaseWorkflow(workflowResp.status !== 204 ? workflowResp.data : null);
    } catch (err) {
      const responseMessage = typeof err.response?.data === 'string'
        ? err.response.data
        : err.response?.data?.message;
      setScheduleError(responseMessage || 'Failed to save workflow schedule item');
    } finally {
      setScheduleLoading(false);
    }
  };

  const handleUpdateScheduleStatus = async (itemId, status) => {
    setScheduleLoading(true);
    setScheduleError('');
    try {
      await caseService.updateWorkflowScheduleItemStatus(id, itemId, status);
      await loadWorkflowScheduleItems();
    } catch (err) {
      const responseMessage = typeof err.response?.data === 'string'
        ? err.response.data
        : err.response?.data?.message;
      setScheduleError(responseMessage || 'Failed to update schedule status');
    } finally {
      setScheduleLoading(false);
    }
  };

  const touchPointCount = touchPointStages.reduce(
    (total, stage) => total + (stage.touchPoints?.length || 0),
    0
  );
  const selectedTouchPointStage = touchPointStages.find((stage) => stage.stageNumber === activeTouchPointStage)
    || touchPointStages[0];

  const formatTouchPointDate = (value) => {
    if (!value) return 'Time not recorded';
    return new Date(value).toLocaleString();
  };

  const displayDraftContent = (value) => {
    if (!value || typeof value !== 'string') {
      return value || '';
    }

    const trimmed = value.trim();
    if (!trimmed.startsWith('{')) {
      return value;
    }

    try {
      const parsed = JSON.parse(trimmed);
      if (parsed.draftContent) return parsed.draftContent;
      if (parsed.draft) return parsed.draft;
      if (parsed.content) return parsed.content;
      if (Array.isArray(parsed.output)) {
        const text = parsed.output
          .flatMap((item) => Array.isArray(item.content) ? item.content : [])
          .map((part) => part.text || part.output_text || '')
          .filter(Boolean)
          .join('\n');
        return text || value;
      }
    } catch (err) {
      return value;
    }

    return value;
  };

  const normalizeBillingProfile = (profile) => ({
    ...profile,
    hourlyRate: numberOrNull(profile.hourlyRate),
    flatFeeAmount: numberOrNull(profile.flatFeeAmount),
    retainerAmount: numberOrNull(profile.retainerAmount),
    subscriptionAmount: numberOrNull(profile.subscriptionAmount),
    contingencyPercentage: numberOrNull(profile.contingencyPercentage),
    recoveryAmount: numberOrNull(profile.recoveryAmount),
    hybridBaseFee: numberOrNull(profile.hybridBaseFee),
  });

  const normalizeTimeEntry = (entry) => ({
    ...entry,
    hours: numberOrNull(entry.hours),
    rate: numberOrNull(entry.rate),
  });

  const workflowTabs = [
    { key: 'aiCaseWorkflow', label: 'AI Case Workflow' },
  ];
  const workflowScheduleLabels = {
    SCHEDULED_EVENT: {
      title: 'Scheduled Events',
      description: 'Track meetings with clients, witnesses, opposing parties, and other case participants.',
      partyLabel: 'Party',
      titlePlaceholder: 'Client intake meeting',
    },
    COURT_APPEARANCE: {
      title: 'Court Appearances',
      description: 'Track hearings, conferences, appearances, and other court dates.',
      partyLabel: 'Court / Judge',
      titlePlaceholder: 'Status conference',
    },
    DEADLINE: {
      title: 'Deadlines',
      description: 'Track filing deadlines, response dates, discovery due dates, and internal due dates.',
      partyLabel: 'Responsible Party',
      titlePlaceholder: 'Discovery response due',
    },
  };
  const calendarTabs = [
    { key: 'SCHEDULED_EVENT', label: 'Scheduled Events' },
    { key: 'COURT_APPEARANCE', label: 'Court Appearances' },
    { key: 'DEADLINE', label: 'Deadlines' },
  ];
  const activeScheduleCategory = activeTab === 'calendar' ? activeCalendarTab : null;
  const activeScheduleConfig = workflowScheduleLabels[activeScheduleCategory];
  const activeScheduleItems = workflowScheduleItems.filter((item) => item.category === activeScheduleCategory);

  const normalizeScheduleItem = (item, category) => ({
    ...item,
    category,
    endAt: item.endAt || null,
    reminderAt: item.reminderAt || null,
  });

  const formatScheduleDate = (value) => value ? new Date(value).toLocaleString() : '-';

  const numberOrNull = (value) => value === '' || value === null || value === undefined ? null : Number(value);
  const money = (value) => `$${Number(value || 0).toFixed(2)}`;

  if (loading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!caseData) return <div>Case not found</div>;

  return (
    <div className="case-detail-container">
      <h1>{caseData.title}</h1>
      <div className="case-header">
        <span className="case-number">{caseData.caseNumber}</span>
        <span className={`status ${caseData.status.toLowerCase()}`}>
          {formatCaseStatus(caseData.status)}
        </span>
      </div>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'details' ? 'active' : ''}`}
          onClick={() => setActiveTab('details')}
        >
          Case Details
        </button>
        <button
          className={`tab ${activeTab === 'research' ? 'active' : ''}`}
          onClick={() => setActiveTab('research')}
        >
          Research Cases ({researchCases.length})
        </button>
        <button
          className={`tab ${activeTab === 'documents' ? 'active' : ''}`}
          onClick={() => setActiveTab('documents')}
        >
          Documents ({documents.length})
        </button>
        <button
          className={`tab ${activeTab === 'contracts' ? 'active' : ''}`}
          onClick={() => setActiveTab('contracts')}
        >
          Contracts ({contracts.length})
        </button>
        <button
          className={`tab ${activeTab === 'touchPoints' ? 'active' : ''}`}
          onClick={() => setActiveTab('touchPoints')}
        >
          Touch Points ({touchPointCount})
        </button>
        <button
          className={`tab ${activeTab === 'workflow' ? 'active' : ''}`}
          onClick={() => setActiveTab('workflow')}
        >
          Workflow
        </button>
        <button
          className={`tab ${activeTab === 'calendar' ? 'active' : ''}`}
          onClick={() => setActiveTab('calendar')}
        >
          Calendar
        </button>
        <button
          className={`tab ${activeTab === 'billing' ? 'active' : ''}`}
          onClick={() => setActiveTab('billing')}
        >
          Billing
        </button>
        <button
          className={`tab ${activeTab === 'draft' ? 'active' : ''}`}
          onClick={() => setActiveTab('draft')}
        >
          Draft
        </button>
        <button className={`tab ${activeTab === 'humanVerification' ? 'active' : ''}`} onClick={() => setActiveTab('humanVerification')}>
          Human Verification ({humanVerificationTasks.length})
        </button>
      </div>

      <div className="tab-content">
        {successNotice && (
          <div className="ai-action-success" role="status">
            <span>{successNotice}</span>
            <button type="button" aria-label="Dismiss message" onClick={() => setSuccessNotice('')}>Dismiss</button>
          </div>
        )}
        {activeTab === 'details' && (
          <div className="details-panel">
            <div className="detail-group">
              <label>Client Name:</label>
              <p>{caseData.clientName}</p>
            </div>
            <div className="detail-group">
              <label>Client Email:</label>
              <p>{caseData.clientEmail}</p>
            </div>
            <div className="detail-group">
              <label>Client Phone:</label>
              <p>{caseData.clientPhone || 'Not provided'}</p>
            </div>
            <div className="detail-group">
              <label>Case Type:</label>
              <p>{caseData.caseType || 'Not provided'}</p>
            </div>
            <div className="detail-group">
              <label>Jurisdiction:</label>
              <p>{caseData.jurisdiction || 'Not provided'}</p>
            </div>
            <div className="detail-group">
              <label>Opposing Party:</label>
              <p>{caseData.opposingParty || 'Not provided'}</p>
            </div>
            <div className="detail-group">
              <label>Preferred Contact Time:</label>
              <p>{caseData.preferredContactTime || 'Not provided'}</p>
            </div>
            <div className="detail-group">
              <label>Description:</label>
              <p>{caseData.description}</p>
            </div>
          </div>
        )}

        {activeTab === 'research' && (
          <div className="research-panel">
            <div className="research-header">
              <h3>Research Cases</h3>
              <div className="research-actions">
                <select value={selectedResearchProviderId} onChange={(e) => setSelectedResearchProviderId(e.target.value)}>
                  <option value="">Select research provider</option>
                  {researchProviders.map((provider) => (
                    <option key={provider.id} value={provider.id}>{provider.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className="generate-research-btn"
                  onClick={handleGenerateResearch}
                  disabled={researchLoading || researchCases.length >= 5 || !selectedResearchProviderId}
                >
                  {researchLoading ? 'Finding Matches...' : 'Find Similar Cases'}
                </button>
              </div>
            </div>
            {researchError && <p className="error">{researchError}</p>}
            {researchCases.length > 0 ? (
              <div className="research-list">
                {researchCases.map((rc) => (
                  <div key={rc.id} className="research-item">
                    <h4>{rc.caseTitle}</h4>
                    <p>Court: {rc.court}</p>
                    <p>Year: {rc.year}</p>
                    {rc.summary && <p className="research-summary">{rc.summary}</p>}
                    <p>Relevance: {rc.relevance}</p>
                    {rc.sourceUrl && (
                      <a href={rc.sourceUrl} target="_blank" rel="noopener noreferrer">
                        View citation
                      </a>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p>No research cases attached yet</p>
            )}
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="documents-panel">
            <div className="documents-header">
              <h3>Documents</h3>
            </div>
            <form className="document-upload-form" onSubmit={handleUploadDocument}>
              <input type="file" name="file" onChange={handleUploadChange} />
              <select name="documentType" value={uploadForm.documentType} onChange={handleUploadChange}>
                <option value="EVIDENCE">Evidence</option>
                <option value="RESEARCH">Research</option>
                <option value="DRAFT">Draft</option>
                <option value="AI_REVIEW">AI Review</option>
                <option value="OTHER">Other</option>
              </select>
              <input
                name="notes"
                value={uploadForm.notes}
                onChange={handleUploadChange}
                placeholder="Notes"
              />
              <button type="submit" disabled={documentLoading}>
                {documentLoading ? 'Working...' : 'Upload'}
              </button>
            </form>
            {documentError && <p className="error">{documentError}</p>}

            <div className="document-tabs">
              <button type="button" className={activeDocumentTab === 'verification' ? 'active' : ''} onClick={() => setActiveDocumentTab('verification')}>
                AI Verification
              </button>
              <button type="button" className={activeDocumentTab === 'review' ? 'active' : ''} onClick={() => setActiveDocumentTab('review')}>
                AI Review
              </button>
            </div>

            {activeDocumentTab === 'verification' && (
              <div className="verification-panel document-sub-panel">
                <div className="document-review-header">
                  <div>
                    <h4>AI Verification</h4>
                    <p>Select a verification company, submit documents from the list, and keep case-level verification notes here.</p>
                  </div>
                  <select value={selectedCompanyId} onChange={(e) => setSelectedCompanyId(e.target.value)}>
                    <option value="">Select verifier</option>
                    {verificationCompanies.map((company) => (
                      <option key={company.id} value={company.id}>{company.name}</option>
                    ))}
                  </select>
                </div>
                <textarea
                  defaultValue={caseData.verificationNotes || ''}
                  placeholder="Enter verification notes here..."
                  rows="5"
                />
              </div>
            )}

            {activeDocumentTab === 'review' && (
              <div className="document-review-panel">
                <div className="document-review-header">
                  <div>
                    <h4>AI Document Review</h4>
                    <p>Select attached documents, choose an LLM provider, and save the generated summary back to this case.</p>
                  </div>
                  <select value={selectedReviewProviderId} onChange={(e) => setSelectedReviewProviderId(e.target.value)}>
                    <option value="">Select review LLM</option>
                    {reviewProviders.map((provider) => (
                      <option key={provider.id} value={provider.id}>{provider.name}</option>
                    ))}
                  </select>
                </div>
                <textarea
                  value={reviewInstructions}
                  onChange={(e) => setReviewInstructions(e.target.value)}
                  placeholder="Optional review instructions"
                  rows="3"
                />
                <div className="document-review-actions">
                  <span>{selectedReviewDocumentIds.length} selected</span>
                  <button
                    type="button"
                    onClick={handleReviewDocuments}
                    disabled={reviewLoading || !selectedReviewProviderId || selectedReviewDocumentIds.length === 0}
                  >
                    {reviewLoading ? 'Reviewing...' : 'Review Selected Documents'}
                  </button>
                </div>
                {reviewError && <p className="error">{reviewError}</p>}
              </div>
            )}

            {documents.length > 0 ? (
              <div className="documents-table-wrap">
                <table className="documents-table">
                  <thead>
                    <tr>
                      <th>Review</th>
                      <th>File</th>
                      <th>Type</th>
                      <th>Storage</th>
                      <th>Verification Status</th>
                      <th>Company</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.map((doc) => (
                      <tr key={doc.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedReviewDocumentIds.includes(doc.id)}
                            onChange={(e) => toggleReviewDocument(doc.id, e.target.checked)}
                            disabled={reviewLoading}
                          />
                        </td>
                        <td>{doc.fileName}</td>
                        <td>{doc.documentType}</td>
                        <td>{doc.storageProvider || 'LOCAL'}</td>
                        <td>{doc.verificationStatus || 'NOT_SUBMITTED'}</td>
                        <td>{doc.verificationCompanyName || '-'}</td>
                        <td className="document-actions">
                          <button type="button" onClick={() => handleViewDocument(doc)}>View</button>
                          <button type="button" onClick={() => handleDownloadDocument(doc)}>Download</button>
                          <button
                            type="button"
                            onClick={() => handleVerifyDocument(doc.id)}
                            disabled={documentLoading || !selectedCompanyId}
                          >
                            Verify
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>No documents attached yet</p>
            )}
          </div>
        )}

        {activeTab === 'humanVerification' && (
          <div className="human-verification-panel">
            <div className="verification-header"><div><h3>Human Verification</h3><p>Send a limited artifact package to an internal or outsourced reviewer. The attorney keeps final approval.</p></div></div>
            {user?.role === 'ATTORNEY' && <form className="verification-request-form" onSubmit={requestHumanVerification}>
              <select value={humanVerificationForm.taskType} onChange={(e) => setHumanVerificationForm({ ...humanVerificationForm, taskType: e.target.value })}>
                <option value="AI_OUTPUT_REVIEW">AI Output Review</option><option value="RESEARCH_VALIDATION">Research Validation</option><option value="DOCUMENT_VERIFICATION">Document Verification</option><option value="CONTRACT_VALIDATION">Contract Validation</option>
              </select>
              <select value={humanVerificationForm.artifactKey} onChange={(e) => setHumanVerificationForm({ ...humanVerificationForm, artifactKey: e.target.value })}>
                {verificationArtifacts.map((artifact) => <option key={artifact.key} value={artifact.key}>{artifact.label}</option>)}
              </select>
              <input value={humanVerificationForm.assignedOrganization} onChange={(e) => setHumanVerificationForm({ ...humanVerificationForm, assignedOrganization: e.target.value })} placeholder="Review organization" />
              <input type="email" value={humanVerificationForm.assignedReviewerEmail} onChange={(e) => setHumanVerificationForm({ ...humanVerificationForm, assignedReviewerEmail: e.target.value })} placeholder="Reviewer email (required for portal access)" />
              <select value={humanVerificationForm.priority} onChange={(e) => setHumanVerificationForm({ ...humanVerificationForm, priority: e.target.value })}><option value="LOW">Low</option><option value="NORMAL">Normal</option><option value="HIGH">High</option><option value="URGENT">Urgent</option></select>
              <input type="datetime-local" value={humanVerificationForm.dueAt} onChange={(e) => setHumanVerificationForm({ ...humanVerificationForm, dueAt: e.target.value })} />
              <textarea value={humanVerificationForm.instructions} onChange={(e) => setHumanVerificationForm({ ...humanVerificationForm, instructions: e.target.value })} placeholder="What should the reviewer verify?" />
              <button type="submit">Request Human Verification</button>
            </form>}
            {humanVerificationError && <p className="error">{humanVerificationError}</p>}
            <div className="verification-task-list">{humanVerificationTasks.length ? humanVerificationTasks.map((task) => <article key={task.id} className="verification-case-task"><div><strong>{task.taskType.replaceAll('_', ' ')}</strong><span>{task.artifactLabel} · {task.assignedOrganization || 'Unassigned'}</span></div><span className="verification-status">{task.status}</span>{task.reviewerSummary && <p><strong>Reviewer:</strong> {task.reviewerSummary}</p>}{task.attorneyDecision && <p><strong>Attorney decision:</strong> {task.attorneyDecision}</p>}{user?.role === 'ATTORNEY' && task.status === 'SUBMITTED' && <div><button type="button" onClick={() => decideHumanVerification(task.id, 'APPROVED')}>Approve</button><button type="button" onClick={() => decideHumanVerification(task.id, 'CHANGES_REQUESTED')}>Request Changes</button></div>}</article>) : <p>No human verification requests for this case.</p>}</div>
          </div>
        )}

        {activeTab === 'touchPoints' && (
          <div className="touch-points-panel">
            <div className="touch-points-header">
              <h3>Customer Touch Points</h3>
              <span>{touchPointCount} interactions</span>
            </div>
            {touchPointStages.length > 0 ? (
              <>
                <div className="touch-point-tabs">
                  {touchPointStages.map((stage) => (
                    <button
                      key={stage.stageNumber}
                      type="button"
                      className={selectedTouchPointStage?.stageNumber === stage.stageNumber ? 'active' : ''}
                      onClick={() => setActiveTouchPointStage(stage.stageNumber)}
                    >
                      {stage.stageNumber}. {stage.stageName}
                    </button>
                  ))}
                </div>

                {selectedTouchPointStage && (
                  <section className="touch-point-stage">
                    <div className="touch-point-stage-header">
                      <span className="touch-point-stage-number">{selectedTouchPointStage.stageNumber}</span>
                      <div>
                        <h4>{selectedTouchPointStage.stageName}</h4>
                        <p>{selectedTouchPointStage.description}</p>
                      </div>
                    </div>
                    {selectedTouchPointStage.touchPoints?.length > 0 ? (
                      <div className="touch-point-list">
                        {selectedTouchPointStage.touchPoints.map((touchPoint) => (
                          <article key={`${touchPoint.sessionId}-${touchPoint.interactionType}-${touchPoint.id}`} className="touch-point-card">
                            <div className="touch-point-meta">
                              <span className={`touch-point-actor ${touchPoint.actor === 'AI Agent' ? 'agent' : 'customer'}`}>
                                {touchPoint.actor}
                              </span>
                              <span>{touchPoint.interactionType}</span>
                              <time>{formatTouchPointDate(touchPoint.occurredAt)}</time>
                            </div>
                            <p>{touchPoint.content}</p>
                            <div className="touch-point-person">
                              <span>{touchPoint.customerName || 'Customer'}</span>
                              <span>{touchPoint.customerEmail || caseData.clientEmail}</span>
                            </div>
                          </article>
                        ))}
                      </div>
                    ) : (
                      <p className="touch-point-empty">No customer interactions captured for this stage yet.</p>
                    )}
                  </section>
                )}
              </>
            ) : (
              <p>No customer touch points captured yet.</p>
            )}
          </div>
        )}

        {activeTab === 'draft' && (
          <div className="draft-panel">
            <div className="draft-header">
              <h3>Case Draft</h3>
              <div className="draft-actions">
                <select value={selectedDraftProviderId} onChange={(e) => setSelectedDraftProviderId(e.target.value)}>
                  <option value="">Select LLM provider</option>
                  {draftProviders.map((provider) => (
                    <option key={provider.id} value={provider.id}>{provider.name}</option>
                  ))}
                </select>
                <button type="button" onClick={handleGenerateDraft} disabled={draftLoading || !selectedDraftProviderId}>
                  {draftLoading ? 'Drafting...' : 'Generate Draft'}
                </button>
              </div>
            </div>
            {draftError && <p className="error">{draftError}</p>}
            <form onSubmit={handleSaveDraft}>
              <textarea
                value={displayDraftContent(caseData.draftContent)}
                onChange={(e) => setCaseData((prev) => ({ ...prev, draftContent: e.target.value }))}
                placeholder="Generate or enter case draft here..."
                rows="12"
              />
              <button className="save-draft-btn" type="submit" disabled={draftLoading}>
                Save Draft
              </button>
            </form>
          </div>
        )}

        {activeTab === 'workflow' && (
          <div className="workflow-panel">
            <div className="workflow-header">
              <div>
                <h3>AI Case Workflow</h3>
                <p>{caseWorkflow ? caseWorkflow.workflowDefinition?.name : 'No workflow has been started for this case yet.'}</p>
              </div>
              {!caseWorkflow && (
                <button type="button" onClick={handleStartWorkflow}>
                  Start Workflow
                </button>
              )}
              {caseWorkflow && (
                <button type="button" onClick={handleRunWorkflow}>
                  Run Workflow
                </button>
              )}
            </div>
            {workflowError && <p className="error">{workflowError}</p>}
            <div className="workflow-tabs">
              {workflowTabs.map((tab) => (
                <button key={tab.key} type="button" className={activeWorkflowTab === tab.key ? 'active' : ''} onClick={() => setActiveWorkflowTab(tab.key)}>
                  {tab.label}
                </button>
              ))}
            </div>

            {activeWorkflowTab === 'aiCaseWorkflow' && (
              caseWorkflow ? (
                <>
                  <div className="workflow-summary">
                    <div>
                      <span>Status</span>
                      <strong>{caseWorkflow.status}</strong>
                    </div>
                    <div>
                      <span>Current Stage</span>
                      <strong>{caseWorkflow.currentStageName || '-'}</strong>
                    </div>
                    <div>
                      <span>Version</span>
                      <strong>{caseWorkflow.workflowDefinition?.version || '-'}</strong>
                    </div>
                  </div>

                  <h4>Human Tasks</h4>
                  {(caseWorkflow.humanTasks || []).length > 0 ? (
                    <div className="workflow-list">
                      {caseWorkflow.humanTasks.map((task) => (
                        <article key={task.id} className="workflow-card">
                          <div className="workflow-card-meta">
                            <span>{task.status}</span>
                            <span>{task.assignedRole || 'Unassigned'}</span>
                            <time>{task.createdAt ? new Date(task.createdAt).toLocaleString() : '-'}</time>
                          </div>
                          <h5>{task.title}</h5>
                          <p>{task.description}</p>
                          {task.status === 'OPEN' && (
                            <button type="button" className="workflow-task-btn" onClick={() => handleCompleteHumanTask(task.id)}>
                              Complete Task
                            </button>
                          )}
                        </article>
                      ))}
                    </div>
                  ) : (
                    <p>No human tasks are open for this case.</p>
                  )}

                  <h4>Workflow Events</h4>
                  {(caseWorkflow.events || []).length > 0 ? (
                    <div className="workflow-list">
                      {caseWorkflow.events.map((event) => (
                        <article key={event.id} className="workflow-card">
                          <div className="workflow-card-meta">
                            <span>{event.eventType?.replaceAll('_', ' ')}</span>
                            <span>{event.actor || 'System'}</span>
                            <time>{event.createdAt ? new Date(event.createdAt).toLocaleString() : '-'}</time>
                          </div>
                          <h5>{event.stageName || 'Workflow'}</h5>
                          <p>{event.summary}</p>
                        </article>
                      ))}
                    </div>
                  ) : (
                    <p>No workflow events captured yet.</p>
                  )}
                </>
              ) : (
                <p>Start the active AI-first playbook to attach orchestration tracking to this case.</p>
              )
            )}

          </div>
        )}

        {activeTab === 'calendar' && activeScheduleConfig && (
          <div className="calendar-panel">
            <div className="calendar-header">
              <div>
                <h3>Case Calendar</h3>
                <p>Manage case meetings, court dates, and deadlines in one place.</p>
              </div>
            </div>
            <div className="calendar-tabs">
              {calendarTabs.map((tab) => (
                <button key={tab.key} type="button" className={activeCalendarTab === tab.key ? 'active' : ''} onClick={() => setActiveCalendarTab(tab.key)}>
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="workflow-schedule-panel">
              <div>
                <h4>{activeScheduleConfig.title}</h4>
                <p>{activeScheduleConfig.description}</p>
              </div>
              {scheduleError && <p className="error">{scheduleError}</p>}
              <form className="workflow-schedule-form" onSubmit={handleSaveScheduleItem}>
                <div className="workflow-schedule-grid">
                  <label>Title<input name="title" value={scheduleForm.title} onChange={handleScheduleChange} placeholder={activeScheduleConfig.titlePlaceholder} required /></label>
                  <label>Date / Time<input name="scheduledAt" type="datetime-local" value={scheduleForm.scheduledAt} onChange={handleScheduleChange} required /></label>
                  <label>End Time<input name="endAt" type="datetime-local" value={scheduleForm.endAt} onChange={handleScheduleChange} /></label>
                  <label>Reminder<input name="reminderAt" type="datetime-local" value={scheduleForm.reminderAt} onChange={handleScheduleChange} /></label>
                  <label>Location<input name="location" value={scheduleForm.location} onChange={handleScheduleChange} placeholder="Office, courthouse, Zoom, phone" /></label>
                  <label>{activeScheduleConfig.partyLabel}<input name="partyName" value={scheduleForm.partyName} onChange={handleScheduleChange} /></label>
                  <label>Priority
                    <select name="priority" value={scheduleForm.priority} onChange={handleScheduleChange}>
                      <option value="NORMAL">Normal</option>
                      <option value="HIGH">High</option>
                      <option value="URGENT">Urgent</option>
                    </select>
                  </label>
                </div>
                <textarea name="description" value={scheduleForm.description} onChange={handleScheduleChange} placeholder="Notes, agenda, required prep, or filing details" rows="3" />
                <button type="submit" disabled={scheduleLoading}>
                  {scheduleLoading ? 'Saving...' : `Add ${activeScheduleConfig.title.slice(0, -1)}`}
                </button>
              </form>

              {activeScheduleItems.length > 0 ? (
                <div className="workflow-list">
                  {activeScheduleItems.map((item) => (
                    <article key={item.id} className="workflow-card schedule-card">
                      <div className="workflow-card-meta">
                        <span>{item.status}</span>
                        <span>{item.priority}</span>
                        <time>{formatScheduleDate(item.scheduledAt)}</time>
                      </div>
                      <h5>{item.title}</h5>
                      <p>{item.description || 'No notes added.'}</p>
                      <div className="schedule-card-details">
                        {item.endAt && <span>Ends: {formatScheduleDate(item.endAt)}</span>}
                        {item.reminderAt && <span>Reminder: {formatScheduleDate(item.reminderAt)}</span>}
                        {item.location && <span>Location: {item.location}</span>}
                        {item.partyName && <span>{activeScheduleConfig.partyLabel}: {item.partyName}</span>}
                      </div>
                      <div className="schedule-card-actions">
                        <button type="button" onClick={() => handleUpdateScheduleStatus(item.id, 'OPEN')} disabled={scheduleLoading || item.status === 'OPEN'}>Open</button>
                        <button type="button" onClick={() => handleUpdateScheduleStatus(item.id, 'COMPLETED')} disabled={scheduleLoading || item.status === 'COMPLETED'}>Complete</button>
                        <button type="button" onClick={() => handleUpdateScheduleStatus(item.id, 'CANCELLED')} disabled={scheduleLoading || item.status === 'CANCELLED'}>Cancel</button>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <p>No {activeScheduleConfig.title.toLowerCase()} have been added yet.</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'billing' && (
          <div className="billing-panel">
            <div className="billing-header">
              <div>
                <h3>Billing</h3>
                <p>Configure billing, track human effort, and generate invoices attached to this case.</p>
              </div>
              <button type="button" onClick={handleGenerateInvoice} disabled={billingLoading || !billingSummary?.profile}>
                {billingLoading ? 'Working...' : 'Generate Invoice'}
              </button>
            </div>
            {billingError && <p className="error">{billingError}</p>}
            <div className="billing-metrics">
              <div><span>Total Hours</span><strong>{billingSummary?.totalTrackedHours || 0}</strong></div>
              <div><span>Unbilled Billable Hours</span><strong>{billingSummary?.unbilledBillableHours || 0}</strong></div>
              <div><span>Total Invoiced</span><strong>{money(billingSummary?.totalInvoiced)}</strong></div>
              <div><span>Total Paid</span><strong>{money(billingSummary?.totalPaid)}</strong></div>
              <div><span>Outstanding</span><strong>{money(billingSummary?.totalOutstanding)}</strong></div>
            </div>

            <div className="billing-tabs">
              <button type="button" className={activeBillingTab === 'setup' ? 'active' : ''} onClick={() => setActiveBillingTab('setup')}>
                Billing Setup
              </button>
              <button type="button" className={activeBillingTab === 'time' ? 'active' : ''} onClick={() => setActiveBillingTab('time')}>
                Time Entries
              </button>
              <button type="button" className={activeBillingTab === 'invoices' ? 'active' : ''} onClick={() => setActiveBillingTab('invoices')}>
                Invoices
              </button>
            </div>

            {activeBillingTab === 'setup' && (
              <form className="billing-form" onSubmit={handleSaveBillingProfile}>
                <h4>Billing Setup</h4>
                <div className="billing-grid">
                  <label>Billing Type
                    <select name="billingType" value={billingProfileForm.billingType} onChange={handleBillingProfileChange}>
                      <option value="HOURLY">Hourly Billing</option>
                      <option value="FLAT_FEE">Fixed / Flat-Fee</option>
                      <option value="SUBSCRIPTION_RETAINER">Subscription / Retainer</option>
                      <option value="CONTINGENCY">Contingency Fee</option>
                      <option value="HYBRID">Hybrid Model</option>
                    </select>
                  </label>
                  <label>Hourly Rate<input name="hourlyRate" type="number" step="0.01" value={billingProfileForm.hourlyRate} onChange={handleBillingProfileChange} /></label>
                  <label>Flat Fee<input name="flatFeeAmount" type="number" step="0.01" value={billingProfileForm.flatFeeAmount} onChange={handleBillingProfileChange} /></label>
                  <label>Retainer Amount<input name="retainerAmount" type="number" step="0.01" value={billingProfileForm.retainerAmount} onChange={handleBillingProfileChange} /></label>
                  <label>Subscription Amount<input name="subscriptionAmount" type="number" step="0.01" value={billingProfileForm.subscriptionAmount} onChange={handleBillingProfileChange} /></label>
                  <label>Interval<input name="subscriptionInterval" value={billingProfileForm.subscriptionInterval} onChange={handleBillingProfileChange} /></label>
                  <label>Contingency %<input name="contingencyPercentage" type="number" step="0.01" value={billingProfileForm.contingencyPercentage} onChange={handleBillingProfileChange} /></label>
                  <label>Recovery Amount<input name="recoveryAmount" type="number" step="0.01" value={billingProfileForm.recoveryAmount} onChange={handleBillingProfileChange} /></label>
                  <label>Hybrid Base Fee<input name="hybridBaseFee" type="number" step="0.01" value={billingProfileForm.hybridBaseFee} onChange={handleBillingProfileChange} /></label>
                  <label>Recipient Name<input name="invoiceRecipientName" value={billingProfileForm.invoiceRecipientName} onChange={handleBillingProfileChange} /></label>
                  <label>Recipient Email<input name="invoiceRecipientEmail" value={billingProfileForm.invoiceRecipientEmail} onChange={handleBillingProfileChange} /></label>
                </div>
                <textarea name="billingNotes" value={billingProfileForm.billingNotes} onChange={handleBillingProfileChange} placeholder="Billing notes" rows="3" />
                <button type="submit" disabled={billingLoading}>{billingLoading ? 'Saving...' : 'Save Billing Setup'}</button>
              </form>
            )}

            {activeBillingTab === 'time' && (
              <>
                <form className="billing-form" onSubmit={handleAddTimeEntry}>
                  <h4>Track Time</h4>
                  <div className="billing-grid">
                    <label>Work Date<input name="workDate" type="date" value={timeEntryForm.workDate} onChange={handleTimeEntryChange} required /></label>
                    <label>Activity<input name="activityType" value={timeEntryForm.activityType} onChange={handleTimeEntryChange} /></label>
                    <label>Hours<input name="hours" type="number" step="0.01" value={timeEntryForm.hours} onChange={handleTimeEntryChange} required /></label>
                    <label>Rate<input name="rate" type="number" step="0.01" value={timeEntryForm.rate} onChange={handleTimeEntryChange} /></label>
                    <label className="checkbox-label"><input name="billable" type="checkbox" checked={timeEntryForm.billable} onChange={handleTimeEntryChange} /> Billable</label>
                  </div>
                  <textarea name="description" value={timeEntryForm.description} onChange={handleTimeEntryChange} placeholder="Describe the work performed" rows="3" required />
                  <button type="submit" disabled={billingLoading}>Add Time Entry</button>
                </form>

                <h4>Time Entries</h4>
                <div className="documents-table-wrap">
                  <table className="documents-table">
                    <thead><tr><th>Date</th><th>User</th><th>Description</th><th>Hours</th><th>Rate</th><th>Billable</th><th>Status</th></tr></thead>
                    <tbody>
                      {(billingSummary?.timeEntries || []).length === 0 ? (
                        <tr><td colSpan="7">No time entries yet.</td></tr>
                      ) : billingSummary.timeEntries.map((entry) => (
                        <tr key={entry.id}>
                          <td>{entry.workDate}</td><td>{entry.userName}</td><td>{entry.description}</td><td>{entry.hours}</td><td>{money(entry.rate)}</td><td>{entry.billable ? 'Yes' : 'No'}</td><td>{entry.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {activeBillingTab === 'invoices' && (
              <>
                <div className="billing-invoice-toolbar">
                  <h4>Invoices</h4>
                  <select value={selectedPaymentProcessorId} onChange={(e) => setSelectedPaymentProcessorId(e.target.value)}>
                    <option value="">Mock payment processor</option>
                    {paymentProcessors.map((provider) => (
                      <option key={provider.id} value={provider.id}>{provider.name}</option>
                    ))}
                  </select>
                </div>
                <div className="documents-table-wrap">
                  <table className="documents-table">
                    <thead><tr><th>Invoice</th><th>Status</th><th>Recipient</th><th>Total</th><th>Paid</th><th>Balance</th><th>Due</th><th>Payments</th><th></th></tr></thead>
                    <tbody>
                      {(billingSummary?.invoices || []).length === 0 ? (
                        <tr><td colSpan="9">No invoices generated yet.</td></tr>
                      ) : billingSummary.invoices.map((invoice) => (
                        <tr key={invoice.id}>
                          <td>{invoice.invoiceNumber}</td><td>{invoice.status}</td><td>{invoice.recipientEmail}</td><td>{money(invoice.totalAmount)}</td><td>{money(invoice.paidAmount)}</td><td>{money(invoice.balanceAmount)}</td><td>{invoice.dueDate}</td>
                          <td>
                            {(invoice.payments || []).length === 0 ? '-' : (
                              <div className="invoice-payments">
                                {invoice.payments.map((payment) => (
                                  <div key={payment.id}>
                                    <strong>{payment.status}</strong> {money(payment.amount)}
                                    {payment.status !== 'PAID' && (
                                      <button type="button" onClick={() => handleMarkPaymentPaid(invoice.id, payment.id)} disabled={billingLoading}>
                                        Mark Paid
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="document-actions">
                            {invoice.documentId && <button type="button" onClick={() => handleDownloadDocument({ id: invoice.documentId, fileName: `${invoice.invoiceNumber}.txt` })}>Download</button>}
                            <button type="button" onClick={() => handleSendInvoice(invoice.id)} disabled={billingLoading || ['SENT', 'PAID', 'PARTIALLY_PAID'].includes(invoice.status)}>Send</button>
                            <button type="button" onClick={() => handleCreatePaymentLink(invoice)} disabled={billingLoading || Number(invoice.balanceAmount || 0) <= 0}>Pay Link</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'contracts' && (
          <div className="contracts-panel">
            <div className="documents-header">
              <h3>Contracts</h3>
              <select value={selectedContractReviewProviderId} onChange={(e) => setSelectedContractReviewProviderId(e.target.value)}>
                <option value="">Select contract review LLM</option>
                {contractReviewProviders.map((provider) => (
                  <option key={provider.id} value={provider.id}>{provider.name}</option>
                ))}
              </select>
            </div>
            <form className="contract-upload-form" onSubmit={handleUploadContract}>
              <input type="file" name="file" onChange={handleContractChange} />
              <input name="title" value={contractForm.title} onChange={handleContractChange} placeholder="Contract title" />
              <input name="contractType" value={contractForm.contractType} onChange={handleContractChange} placeholder="Type" />
              <input name="counterparty" value={contractForm.counterparty} onChange={handleContractChange} placeholder="Counterparty" />
              <input name="notes" value={contractForm.notes} onChange={handleContractChange} placeholder="Notes" />
              <button type="submit" disabled={contractLoading}>
                {contractLoading ? 'Working...' : 'Add Contract'}
              </button>
            </form>
            <div className="document-review-panel">
              <div className="document-review-header">
                <div>
                  <h4>AI Contract Review</h4>
                  <p>Use the selected provider to summarize obligations, risks, deadlines, missing terms, and follow-up items.</p>
                </div>
              </div>
              <textarea
                value={contractReviewInstructions}
                onChange={(e) => setContractReviewInstructions(e.target.value)}
                placeholder="Optional contract review instructions"
                rows="3"
              />
              {contractError && <p className="error">{contractError}</p>}
            </div>
            {contracts.length > 0 ? (
              <div className="documents-table-wrap">
                <table className="documents-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Type</th>
                      <th>Counterparty</th>
                      <th>Review Status</th>
                      <th>Review Summary</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {contracts.map((contract) => (
                      <tr key={contract.id}>
                        <td>{contract.title}</td>
                        <td>{contract.contractType || '-'}</td>
                        <td>{contract.counterparty || '-'}</td>
                        <td>{contract.reviewStatus || 'NOT_REVIEWED'}</td>
                        <td className="summary-cell">{contract.reviewSummary || '-'}</td>
                        <td className="document-actions">
                          <button type="button" onClick={() => handleViewContract(contract)}>View</button>
                          <button type="button" onClick={() => handleDownloadContract(contract)}>Download</button>
                          <button
                            type="button"
                            onClick={() => handleReviewContract(contract.id)}
                            disabled={contractLoading || !selectedContractReviewProviderId}
                          >
                            Review
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>No contracts attached yet</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
